import { faUserPlus, faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
	ACCESS_RIGHT_AREAS,
	ACCESS_RIGHT_TYPES,
	checkPermission,
} from "../../../../constant/permissions";
import { useListPresetsQuery } from "../../../../store/services/projectPermissionPresets";
import SelectedUserChips from "./selected-user-chips";
import TabExistingUsers from "./tab-existing-users";
import TabExternalGuest from "./tab-external-guest";

const TAB_EXISTING = "existing";
const TAB_EXTERNAL = "external";

function Step1SelectUser({
	projectId,
	existingMemberIds,
	selectedUsers,
	onSelectedUsersChange,
	selectedPresetId,
	onPresetSelect,
}) {
	const { t } = useTranslation();
	const { user } = useSelector((state) => state.auth) || {};
	
	const canViewUsers = checkPermission(
		user?.permissions,
		ACCESS_RIGHT_AREAS.USERS,
		ACCESS_RIGHT_TYPES.VIEW,
	);
	const [activeTab, setActiveTab] = useState(
		canViewUsers ? TAB_EXISTING : TAB_EXTERNAL,
	);

	const { data: presetsData } = useListPresetsQuery(projectId, {
		skip: !projectId,
	});

	const allPresets = useMemo(
		() => presetsData?.items ?? [],
		[presetsData?.items],
	);

	// Seed a sensible starting preset so Step 2 opens pre-filled and "Next" is
	// enabled. The role is actually chosen/refined on Step 2 (next to the
	// permission matrix it controls), so there is no role picker here. We default
	// to the first system preset - no coupling to any preset name.
	useEffect(() => {
		if (selectedPresetId || !allPresets.length) {
			return;
		}
		const defaultPreset =
			allPresets.find((p) => p.isSystem) ?? allPresets[0];
		if (defaultPreset) {
			onPresetSelect(defaultPreset);
		}
	}, [selectedPresetId, allPresets, onPresetSelect]);

	const removeUser = (email) => {
		onSelectedUsersChange(
			selectedUsers.filter((u) => u.email !== email),
		);
	};

	return (
		<div className="add-member-wizard__step1">
			<div className="add-member-wizard__tab-bar">
				{canViewUsers && (
					<button
						type="button"
						className={`add-member-wizard__tab-btn ${activeTab === TAB_EXISTING ? "active" : ""}`}
						onClick={() => setActiveTab(TAB_EXISTING)}
					>
						<FontAwesomeIcon icon={faUsers} />
						{t("projects.members.tab-existing-users")}
					</button>
				)}
				<button
					type="button"
					className={`add-member-wizard__tab-btn ${activeTab === TAB_EXTERNAL ? "active" : ""}`}
					onClick={() => setActiveTab(TAB_EXTERNAL)}
				>
					<FontAwesomeIcon icon={faUserPlus} />
					{t("projects.members.tab-external-guest")}
				</button>
			</div>

			{canViewUsers && (
				<div
					className="add-member-wizard__tab-content"
					hidden={activeTab !== TAB_EXISTING}
				>
					<TabExistingUsers
						existingMemberIds={existingMemberIds}
						selectedUsers={selectedUsers}
						onSelectedUsersChange={onSelectedUsersChange}
					/>
				</div>
			)}
			<div
				className="add-member-wizard__tab-content"
				hidden={activeTab !== TAB_EXTERNAL}
			>
				<TabExternalGuest
					existingMemberIds={existingMemberIds}
					selectedUsers={selectedUsers}
					onSelectedUsersChange={onSelectedUsersChange}
				/>
			</div>

			<SelectedUserChips users={selectedUsers} onRemove={removeUser} />
		</div>
	);
}

export default Step1SelectUser;
